import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock3,
  MapPin,
  RefreshCw,
} from "lucide-react";

import { STATUS } from "../config/taskData";
import { API_BASE } from "../config/appConfig";
import { formatDate, getDeadlineCountdown, getDeadlineParts } from "../utils/tasks";
import { InlineIcon, UiIcon } from "./Icon";

const BACKEND_TO_UI_STATUS = {
  new: "new",
  accepted: "in_progress",
  completed: "done",
  issue: "problem",
};

const UI_TO_BACKEND_STATUS = {
  in_progress: "accepted",
  done: "completed",
  problem: "issue",
};

function normalizeTask(task) {
  const raw = task.deadline ? task.deadline : null;
  const { date, time } = getDeadlineParts(raw);

  return {
    id: task.id,
    access_token: task.access_token,
    name: task.name || task.description || "Bez nazwy",
    desc: task.description || task.name,
    location: task.location || "",
    status: BACKEND_TO_UI_STATUS[task.status] || "new",
    date,
    time,
    deadlineAt: raw,
    event_name: task.event_name || "",
  };
}

export default function TaskTokenScreen({ token }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/tasks/token/${token}/`);
        if (!res.ok) {
          setError("Nie znaleziono zadania.");
          return;
        }
        const data = await res.json();
        setTask(normalizeTask(data));
      } catch {
        setError("Błąd połączenia.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const updateStatus = async (nextUiStatus) => {
    const backendStatus = UI_TO_BACKEND_STATUS[nextUiStatus];
    if (!backendStatus || updating) return;

    setUpdating(true);
    try {
      const res = await fetch(`${API_BASE}/api/tasks/token/${token}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: backendStatus }),
      });
      if (res.ok) {
        const data = await res.json();
        setTask(normalizeTask(data));
      }
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100dvh", fontFamily: "var(--font-body)", color: "var(--color-text-muted)" }}>
        Ładowanie zadania...
      </div>
    );
  }

  if (error || !task) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100dvh", fontFamily: "var(--font-body)", color: "var(--color-danger)", padding: 24, textAlign: "center" }}>
        {error || "Nie znaleziono zadania."}
      </div>
    );
  }

  const st = STATUS[task.status];
  const isDone = task.status === "done";
  const deadlineCountdown = task.deadlineAt ? getDeadlineCountdown(task.deadlineAt) : null;
  const displayDeadlineCountdown = deadlineCountdown?.replace(/^Za\s+/, "");
  const isDeadlineOverdue = deadlineCountdown?.startsWith("Po terminie");

  const actions = {
    new: [
      { label: "Przyjmuję zadanie", icon: Check, next: "in_progress", color: "var(--color-primary)", bg: "var(--color-primary-light)", border: "var(--color-primary-border)" },
    ],
    in_progress: [
      { label: "Zadanie wykonane", icon: Check, next: "done", color: "var(--badge-done-text)", bg: "var(--badge-done-bg)", border: "var(--badge-done-border)" },
      { label: "Zgłoś problem", icon: AlertTriangle, next: "problem", color: "var(--badge-problem-text)", bg: "var(--badge-problem-bg)", border: "var(--badge-problem-border)" },
    ],
    done: [],
    problem: [
      { label: "Wznawiam realizację", icon: RefreshCw, next: "in_progress", color: "var(--badge-progress-text)", bg: "var(--badge-progress-bg)", border: "var(--badge-progress-border)" },
    ],
  };

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "var(--color-bg-app)", minHeight: "100dvh", maxWidth: 480, width: "calc(100% - 16px)", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ background: "var(--color-bg-card)", borderBottom: "1px solid var(--color-border)", padding: "20px 20px 16px" }}>
        <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6, letterSpacing: "0.05em" }}>
          ZADANIE DO WYKONANIA
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-h2)", fontWeight: "var(--font-semibold)", color: "var(--color-text-primary)", lineHeight: 1.2 }}>
          {task.name}
        </div>
      </div>

      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Status badge */}
        <div style={{ background: st.bg, borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, border: `1px solid ${st.border}` }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: st.dot, display: "inline-block", flexShrink: 0 }} />
          <span style={{ color: st.color, fontWeight: "var(--font-semibold)", fontSize: "var(--text-xs)" }}>{st.label}</span>
        </div>

        {/* Task details */}
        <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 20 }}>
          <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-h3)", color: "var(--color-text-primary)", fontWeight: "var(--font-semibold)", lineHeight: "var(--leading-text)", margin: "0 0 12px" }}>
            {task.desc || "Brak opisu"}
          </p>
          {(task.date || task.time) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "flex-start",
                  gap: 8,
                  background: "var(--color-bg-subtle)",
                  color: !isDone && isDeadlineOverdue ? "var(--color-warning)" : "var(--color-text-muted)",
                  padding: "8px 10px",
                  borderRadius: "var(--radius-md)",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-medium)",
                  border: "1px solid var(--color-border)",
                  lineHeight: 1.4,
                }}
              >
                <InlineIcon Icon={Clock3} style={{ marginTop: 1 }} />
                <div>
                  <div>
                    Do: {task.date && formatDate(task.deadlineAt || task.date)}
                    {task.time ? ` ${task.time}` : ""}
                  </div>
                  {!isDone && task.date && deadlineCountdown && (
                    <div>
                      Pozostało: {isDeadlineOverdue ? "Po terminie" : displayDeadlineCountdown}
                    </div>
                  )}
                </div>
              </div>
              {task.location && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "var(--color-bg-subtle)",
                    color: "var(--color-text-muted)",
                    padding: "4px 10px",
                    borderRadius: "var(--radius-md)",
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--font-medium)",
                    border: "1px solid var(--color-border)",
                  }}
                >
                  <InlineIcon Icon={MapPin} style={{ marginRight: 2 }} />
                  {task.location}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {isDone ? (
          <div style={{ background: "var(--badge-done-bg)", border: "1px solid var(--badge-done-border)", borderRadius: "var(--radius-lg)", padding: 24, textAlign: "center" }}>
            <div style={{ marginBottom: 8, display: "inline-flex" }}><UiIcon Icon={CheckCircle2} /></div>
            <div style={{ color: "var(--color-success)", fontWeight: "var(--font-semibold)", fontSize: "var(--text-body)" }}>Zadanie zakończone</div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-sm)", marginTop: 4 }}>Świetna robota!</div>
          </div>
        ) : (
          actions[task.status]?.map((a) => (
            <button
              key={a.next}
              onClick={() => updateStatus(a.next)}
              disabled={updating}
              style={{ background: a.bg, color: a.color, border: `1.5px solid ${a.border}`, borderRadius: "var(--radius-lg)", padding: 18, fontWeight: "var(--font-semibold)", fontSize: "var(--text-body)", cursor: updating ? "default" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, opacity: updating ? 0.7 : 1 }}
            >
              <span style={{ display: "inline-flex" }}><UiIcon Icon={a.icon} /></span>
              {updating ? "Zapisywanie..." : a.label}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
